import type { Building } from "building-types/building/buildingTypes";
import type { BuildingAnalyticEntity } from "building-types/buildingAnalyticEntityTypes";
import {
  isBuildingGeomAttributeTagOfNameAndValueType,
  isBuildingGeomTag,
} from "building-types/buildingGeomTag/buildingGeomTags";
import {
  BALUSTER,
  buildingGeomAttributeTagNew,
  INDEX_ATTRIBUTE,
  INFILL,
  POST,
  RAIL,
  RAILING,
  SOLID,
  type BuildingGeomTag,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import {
  type RailingGeomTag,
  type RailingInfillTag,
  RailingInfillStyle,
  type RailingPostTag,
  type RailingRailTag,
} from "building-types/settings/railingTypes";
import type { RWTable, Table } from "common/data/table";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import { brepNew, type Face, type FaceTopo } from "geom/brep";
import type { Geom } from "geom/geom";
import { line3Slope } from "geom/line3";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3EndPoint, polyLine3Length, polyLine3StartPoint } from "geom/polyLine3";
import { addAnalyticBRep, addAnalyticExtrusion } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import memo from "../utils/memo";

export const RAILING_POST_PLACEMENT_TYPE_ATTRIBUTE = "railingPostPlacementType";
export const RAILING_RAIL_TYPE_ATTRIBUTE = "railingRailType";
export const RAILING_RAIL_HEIGHT_ATTRIBUTE = "railingRailHeight";
export const RAILING_RAIL_WIDTH_ATTRIBUTE = "railingRailWidth";
export const RAILING_RAIL_THICKNESS_ATTRIBUTE = "railingRailThickness";
export const RAILING_RAIL_LENGTH_ATTRIBUTE = "railingRailLength";
export const RAILING_RAIL_SLOPE_ATTRIBUTE = "railingRailSlope";

const isIndexTag = isBuildingGeomAttributeTagOfNameAndValueType(INDEX_ATTRIBUTE, "NUMBER");
export function addRailingAnalyticEntities(
  building: ReturnType<typeof select>,
  buildingEntityHandle: BuildingEntityHandle,
): Table<BuildingAnalyticEntity> {
  const entities: RWTable<BuildingAnalyticEntity> = {};
  if (buildingEntityHandle.entityType !== "RAILING") {
    return entities;
  }

  const railing = building.railings[buildingEntityHandle.id];
  if (!railing) {
    return entities;
  }
  /** This handle allows Studio inspector selection of the parent doc entity. */
  const railingHandle: DocEntityHandle = {
    type: "RAILING",
    id: railing.id,
  };
  /** This handle allows correct filtering for railing building query controllers. */
  const generatedRailingHandle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: buildingEntityHandle,
  };
  const levelHandle = building.levels[railing.levelId]?.handle;
  // All railing parts need a level parent so plan views can hide them with their level.
  const railingPartParents = levelHandle
    ? [railingHandle, generatedRailingHandle, levelHandle]
    : [railingHandle, generatedRailingHandle];
  if (levelHandle) {
    BuildingGenerationContext.indexAddNode(levelHandle);
  }
  for (const {
    group: { geom, tags },
  } of railing.groups) {
    for (let i = 0; i < geom.length; i++) {
      const railingGeom: Geom | undefined = geom[i];
      const tag: RailingGeomTag | undefined = tags[i];
      if (!railingGeom || !tag) {
        continue;
      }
      if (railingGeom.type === "EXTRUSION" && isRailingPostTag(tag)) {
        const analyticTags: BuildingGeomTag[] = [
          RAILING,
          POST,
          buildingGeomAttributeTagNew(
            RAILING_POST_PLACEMENT_TYPE_ATTRIBUTE,
            tag.placementType,
            "RAILING_POST_PLACEMENT_TYPE",
          ),
        ];

        addAnalyticExtrusion(
          "ANALYTIC_RAILING",
          entities,
          "Railing Post",
          railingPartParents,
          railingGeom,
          tag.materialSrc,
          analyticTags,
          undefined,
          undefined,
          undefined,
          true,
        );
        continue;
      }

      if (railingGeom.type === "BREP" && isRailingRailTag(tag)) {
        const geometryBySegmentIndex = new Map<number, { faces: Face[]; faceTopos: FaceTopo[] }>();
        for (let faceIndex = 0; faceIndex < railingGeom.faces.length; faceIndex++) {
          const face = railingGeom.faces[faceIndex];
          const segmentTag = face.tags?.find((faceTag) => isBuildingGeomTag(faceTag) && isIndexTag(faceTag));
          if (!segmentTag) {
            continue;
          }
          const geometry = geometryBySegmentIndex.get(segmentTag.attributeValue) ?? { faces: [], faceTopos: [] };
          geometry.faces.push(face);
          geometry.faceTopos.push(railingGeom.faceTopos[faceIndex]);
          geometryBySegmentIndex.set(segmentTag.attributeValue, geometry);
        }

        for (const [segmentIndex, { faces, faceTopos }] of geometryBySegmentIndex) {
          const segment = tag.segments.find((railSegment) => railSegment.segmentIndex === segmentIndex);
          if (!segment) {
            continue;
          }
          addAnalyticBRep(
            "ANALYTIC_RAILING",
            entities,
            `Railing ${tag.railType === "TOP" ? "Top" : "Bottom"} Rail`,
            railingPartParents,
            brepNew(faces, faceTopos),
            tag.materialSrc,
            undefined,
            undefined,
            [
              RAILING,
              RAIL,
              buildingGeomAttributeTagNew(INDEX_ATTRIBUTE, segmentIndex, "NUMBER"),
              buildingGeomAttributeTagNew(RAILING_RAIL_TYPE_ATTRIBUTE, tag.railType, "RAILING_RAIL_TYPE"),
              buildingGeomAttributeTagNew(RAILING_RAIL_HEIGHT_ATTRIBUTE, worldInchesNew(tag.height), "INCHES"),
              buildingGeomAttributeTagNew(RAILING_RAIL_WIDTH_ATTRIBUTE, worldInchesNew(tag.width), "INCHES"),
              buildingGeomAttributeTagNew(RAILING_RAIL_THICKNESS_ATTRIBUTE, worldInchesNew(tag.thickness), "INCHES"),
              buildingGeomAttributeTagNew(
                RAILING_RAIL_LENGTH_ATTRIBUTE,
                worldInchesNew(polyLine3Length(segment.centerLine)),
                "INCHES",
              ),
              buildingGeomAttributeTagNew(RAILING_RAIL_SLOPE_ATTRIBUTE, railSegmentSlope(segment.centerLine), "NUMBER"),
            ],
          );
        }
        continue;
      }

      if (isRailingInfillTag(tag)) {
        const analyticTags: BuildingGeomTag[] = [
          RAILING,
          INFILL,
          buildingGeomAttributeTagNew(INDEX_ATTRIBUTE, tag.segmentIndex, "NUMBER"),
        ];
        if (tag.infillType === RailingInfillStyle.BALUSTERS) {
          analyticTags.push(BALUSTER);
        } else if (tag.infillType === RailingInfillStyle.SOLID) {
          analyticTags.push(SOLID);
        }
        const name = tag.infillType === RailingInfillStyle.BALUSTERS ? "Railing Baluster" : "Railing Solid Infill";

        if (railingGeom.type === "BREP") {
          addAnalyticBRep(
            "ANALYTIC_RAILING",
            entities,
            name,
            railingPartParents,
            railingGeom,
            tag.materialSrc,
            undefined,
            undefined,
            analyticTags,
          );
        } else if (railingGeom.type === "EXTRUSION") {
          addAnalyticExtrusion(
            "ANALYTIC_RAILING",
            entities,
            name,
            railingPartParents,
            railingGeom,
            tag.materialSrc,
            analyticTags,
            undefined,
            undefined,
            undefined,
            tag.infillType === RailingInfillStyle.BALUSTERS,
          );
        }
      }
    }
  }

  return entities;
}

/**
 * Absolute rise over run of a rail segment, taken end to end so a segment spanning several centerline
 * edges reports the slope of the run as a whole. Matches how the RAILING controller measures a railing
 * segment's slope.
 */
function railSegmentSlope(centerLine: PolyLine3): number {
  const [startX, startY, startZ] = polyLine3StartPoint(centerLine);
  const [endX, endY, endZ] = polyLine3EndPoint(centerLine);
  return Math.abs(line3Slope(startX, startY, startZ, endX, endY, endZ));
}

function isRailingPostTag(tag: RailingGeomTag): tag is RailingPostTag {
  return tag.type === "POST";
}

function isRailingRailTag(tag: RailingGeomTag): tag is RailingRailTag {
  return tag.type === "RAIL";
}

function isRailingInfillTag(tag: RailingGeomTag): tag is RailingInfillTag {
  return tag.type === "INFILL";
}

const select = makeBuildingSelector(["railings", "levels"]);

function generate(building: ReturnType<typeof select>): Pick<Building, "analyticRailingEntities"> {
  const analyticRailingEntities: RWTable<BuildingAnalyticEntity> = {};
  for (const id of Object.keys(building.railings)) {
    Object.assign(analyticRailingEntities, addRailingAnalyticEntities(building, { entityType: "RAILING", id }));
  }
  return { analyticRailingEntities };
}

export default memo(select, generate);
